import React, { useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button, Flex, Spin } from "antd"
import { routes } from "router"
import { Icon } from "ui/Icon"
import { numberToCurrency } from "helpers/string"
import { removeFromWidgetCart, updateWidgetCartItem, widgetCartItemsSelector } from "store/experiences"
import { buildCartItemSummaries, combineCartSummaries } from "../WidgetExperience/pricing"
import { hasCartItem } from "../bookingDescription"
import { CART_HOLD_STATUSES, CART_ITEM_QUERY } from "../constants"
import CartHoldTimer from "../CartHold/CartHoldTimer"
import ReservationExpiredNotice from "../CartHold/ReservationExpiredNotice"
import { getHoldIssue } from "../CartHold/holdIssue"
import useCartHold from "../CartHold/useCartHold"
import useCartExperiences, { EXPERIENCE_FAILED } from "../useCartExperiences"
import CartItem from "./CartItem"
import EmptyCart from "./EmptyCart"
import styles from "./WidgetCart.module.css"

// The cart: the guide's experiences the guest added (private and ticketed), each with its own
// add-ons, then the totals across all of them. It is kept across reloads (useWidgetCartStorage),
// also once the reservation ran out: the items are then marked no longer reserved, and only "Check
// availability & continue" reserves them again (opening the cart reserves nothing).
const WidgetCart = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { uuid } = useParams()
  const cartItems = useSelector(widgetCartItemsSelector)
  const items = useMemo(() => cartItems.filter(hasCartItem), [cartItems])
  const experiences = useCartExperiences(items)

  const rows = useMemo(() => {
    const loaded = items.map((item) => {
      const experience = experiences[item.experienceId]
      const isUnavailable = experience === EXPERIENCE_FAILED
      const loaded = experience && !isUnavailable ? experience : null
      return { item, experience: loaded, isUnavailable, holdIssue: loaded ? getHoldIssue({ item, experience: loaded, t }) : null }
    })
    // Priced together: the card fee is charged once for the cart.
    const ready = loaded.filter((row) => row.experience)
    const summaries = buildCartItemSummaries(ready.map(({ experience, item }) => ({ experience, item })))
    return loaded.map((row) => ({ ...row, summary: row.experience ? summaries[ready.indexOf(row)] : null }))
  }, [items, experiences, t])
  const isLoading = rows.some((row) => !row.isUnavailable && !row.summary)
  const hasUnavailable = rows.some((row) => row.isUnavailable)
  const hasLostHolds = rows.some((row) => row.holdIssue)
  const totals = useMemo(() => combineCartSummaries(rows.filter((row) => row.summary).map((row) => row.summary)), [rows])

  const { isReservationOver } = useCartHold()
  // An item that "Check availability & continue" would reserve again (a lost one says why instead).
  const isNotReserved = (item) => isReservationOver && item.holdStatus !== CART_HOLD_STATUSES.lost
  const proceedToCheckout = () => navigate(routes.widgetCheckout({ uuid }))
  // An item whose places are gone: its experience opens with the item's choices, to save another time.
  const chooseAnotherTime = (item) => navigate(routes.widgetExperience({ uuid, id: item.experienceId }, { [CART_ITEM_QUERY]: item.cartId }))

  const goToListing = () => navigate(routes.widgetListing({ uuid }))
  // Back to the experience when it is the only one in the cart, else to the list.
  const goBack = () => (items.length === 1 ? navigate(routes.widgetExperience({ uuid, id: items[0].experienceId })) : goToListing())

  const header = (
    <Flex align="center" justify="space-between" gap={16} wrap="wrap">
      <Flex align="center" gap={20}>
        <Button
          shape="circle"
          className={styles.backButton}
          aria-label={t("booking_widget.cart.back")}
          onClick={goBack}
          icon={<Icon type="feather" name="ChevronLeft" width={20} height={20} color="currentColor" />}
        />
        <h1 className={styles.title}>{t("booking_widget.cart.title")}</h1>
      </Flex>
      <CartHoldTimer />
    </Flex>
  )

  if (!items.length) {
    return (
      <Flex vertical gap={40}>
        {header}
        <EmptyCart onChooseExperience={goToListing} />
      </Flex>
    )
  }

  if (isLoading) {
    return (
      <Flex vertical gap={40}>
        {header}
        <Flex justify="center">
          <Spin />
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={40}>
      {header}
      <Flex vertical gap={24} className={styles.content}>
        <Flex vertical gap={24}>
          {rows.map(({ item, experience, summary, isUnavailable, holdIssue }, index) => (
            <CartItem
              key={item.cartId}
              item={item}
              experience={experience}
              summary={summary}
              isUnavailable={isUnavailable}
              holdIssue={holdIssue}
              isNotReserved={isNotReserved(item)}
              // The latest item shows its add-ons; the others start folded ("Add-ons · 2 selected").
              isAddOnsOpen={index === rows.length - 1}
              onRemove={() => dispatch(removeFromWidgetCart(item.cartId))}
              onChooseAnotherTime={() => chooseAnotherTime(item)}
              onAddOnsChange={(addOns) => dispatch(updateWidgetCartItem(item.cartId, { addOns }))}
            />
          ))}
        </Flex>

        <Flex vertical gap={4} className={styles.totals}>
          <Flex justify="space-between">
            <span>{t("booking_widget.cart.experiences")}</span>
            <span>{numberToCurrency(totals.experienceTotal)}</span>
          </Flex>
          <Flex justify="space-between">
            <span>{t("booking_widget.cart.add_ons_total")}</span>
            <span>{numberToCurrency(totals.addOnsTotal)}</span>
          </Flex>
          <Flex justify="space-between">
            <span>{t("booking_widget.cart.add_on_fees")}</span>
            <span>{numberToCurrency(totals.addOnFees)}</span>
          </Flex>
          <div className={styles.divider} />
          <Flex justify="space-between" align="baseline" className={styles.totalRow}>
            <span>{t("booking_widget.cart.total")}</span>
            <span>
              {totals.originalTotal != null && <span className={styles.struck}>{numberToCurrency(totals.originalTotal)}</span>}
              {numberToCurrency(totals.total)}
            </span>
          </Flex>
        </Flex>

        {hasUnavailable && (
          <span className={styles.unavailableNote} role="alert">
            {t("booking_widget.cart.remove_unavailable")}
          </span>
        )}
        {!hasUnavailable && hasLostHolds && (
          <span className={styles.unavailableNote} role="alert">
            {t("booking_widget.cart.remove_lost")}
          </span>
        )}
        {isReservationOver && <ReservationExpiredNotice />}
        {/* Pinned to the bottom of the screen on a phone (Figma 6058:142375). */}
        <div className={styles.actions} data-pinned-actions="">
          <Button shape="round" size="large" color="primary" variant="outlined" onClick={goToListing}>
            {t("booking_widget.cart.add_another")}
          </Button>
          <Button type="primary" shape="round" size="large" disabled={hasUnavailable || hasLostHolds || isReservationOver} onClick={proceedToCheckout}>
            {t("booking_widget.cart.checkout")}
          </Button>
        </div>
      </Flex>
    </Flex>
  )
}

export default WidgetCart
